import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useReducedMotion } from '../hooks/useReducedMotion';

function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

const Hero = () => {
  const prefersReduced = useReducedMotion();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (prefersReduced) return;
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReduced]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-10">
      {/* Background image with parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(/images/crowd-festival-dinosaur-enthusiasts-007.jpeg)',
          transform: prefersReduced ? 'none' : `translateY(${scrollY * 0.3}px)`,
        }}
      />

      {/* Gradient overlay - darker for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/50" />

      {/* Film grain */}
      <div className="film-grain absolute inset-0 pointer-events-none" />

      {/* Floating leaf particles */}
      {!prefersReduced && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute text-jungle-400/20 animate-leaf-drift"
              style={{
                left: `${15 + i * 15}%`,
                top: `${10 + (i % 3) * 25}%`,
                animationDelay: `${i * 1.3}s`,
                fontSize: `${18 + i * 4}px`,
              }}
            >
              🌿
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.p
          initial={prefersReduced ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-mono text-sm sm:text-base uppercase tracking-[0.3em] text-amber mb-4 font-semibold"
        >
          ⚡ Premium Roaming Dinosaur Entertainment ⚡
        </motion.p>

        <motion.h1
          initial={prefersReduced ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-display font-bold text-cream
                     leading-[0.9] tracking-tight text-shadow-dark mb-6"
          style={{ fontSize: 'clamp(2rem, 6.5vw, 4.25rem)' }}
        >
          A <span style={{
            background: 'linear-gradient(to bottom, #FF4500 50%, #FFD700 50%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold',
            textShadow: 'none',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
          }}>RoARsOME</span> Dinosaur Adventure That Turns Your Event Into A{' '}
          <span className="text-yellow-bright">Showpny Attraction</span>
        </motion.h1>

        <motion.p
          initial={prefersReduced ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg sm:text-xl md:text-2xl text-cream/80 max-w-2xl mx-auto mb-10
                     font-body leading-relaxed"
        >
          Jurassic Jack delivers all-day roaming dinosaurs, hands-on fossil activities, and high-energy family entertainment — designed especially for agricultural shows, major community events, festivals, and school programs.
        </motion.p>

        {/* CTA Buttons - moved up before stats */}
        <motion.div
          initial={prefersReduced ? {} : { opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
        >
          <Link to="/contact" className="btn-primary text-lg px-10 py-4">
            Request a Quote
          </Link>
          <Link to="/contact" className="btn-secondary text-lg px-10 py-4">
            Check Availability
          </Link>
        </motion.div>

        {/* Social proof counters */}
        <motion.div
          initial={prefersReduced ? {} : { opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 pb-4"
        >
          {[
            { target: 200, suffix: '+', label: 'Events' },
            { target: 10000, suffix: '+', label: 'Children Delighted' },
            { target: 50, suffix: '+', label: 'Shows & Schools' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-display text-3xl sm:text-4xl md:text-5xl text-amber font-bold">
                <AnimatedCounter target={stat.target} suffix={stat.suffix} />
              </p>
              <p className="text-sm text-cream/50 font-mono uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
